import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { CaretDownFill, CaretUpFill } from 'react-bootstrap-icons';
import MarketplaceSelector from '../components/MarketplaceSelector';
import ErrorMessage from '../components/ErrorMessage';
import { formatError, parseApiError } from '../utils/errorHandling';
import APIResultViewer from '../components/APIResultViewer';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { 
  selectConfigurations, 
  selectIsLoading, 
  selectErrors,
  fetchConfigurationsStart,
  fetchConfigurationsSuccess,
  fetchConfigurationsFailure,
  instanceOfExtendedWarrantyConfiguration
} from '../actions/configurationSlice';
import { getConfiguration } from '../api/WsaToolsFacade';
import { fetchMarketplaces } from '../actions/MarketplaceList';
import type { ColumnDescription } from 'react-bootstrap-table-next';

const ConfigurationViewerPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const configurations = useAppSelector(selectConfigurations);
  const isLoading = useAppSelector(selectIsLoading);
  const errors = useAppSelector(selectErrors);
  
  const [selectedMarketplace, setSelectedMarketplace] = useState('');
  const [asin, setAsin] = useState('');
  const [configType, setConfigType] = useState<string>('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  // Get marketplace list for country code lookup
  const marketplaceList = fetchMarketplaces();
  // Create a lookup map for marketplace ID to country code
  const marketplaceToCountryMap = marketplaceList.reduce((map, marketplace) => {
    map[marketplace.obfuscated_marketplace_id] = marketplace.country_code;
    return map;
  }, {} as Record<string, string>);

  // Define table columns - updated to match the direct backend response structure
  // Component to display covered device configs
  const CoveredDevicesExpander = ({ coveredDeviceConfigs }: { coveredDeviceConfigs?: any[] }) => {
    const [expanded, setExpanded] = useState(false);
    
    if (!coveredDeviceConfigs || coveredDeviceConfigs.length === 0) {
      return <span>No covered devices</span>;
    }
    
    return (
      <div>
        <Button 
          variant="link" 
          onClick={() => setExpanded(!expanded)} 
          className="p-0 text-decoration-none"
        >
          {expanded ? '▼' : '►'} {coveredDeviceConfigs.length} Covered Device{coveredDeviceConfigs.length !== 1 ? 's' : ''}
        </Button>
        
        {expanded && (
          <div className="mt-2 border-start ps-3">
            <ul className="list-unstyled">
              {coveredDeviceConfigs.map((device, index) => (
                <li key={index} className="mb-1">
                  <strong>Device ASIN:</strong> {device.deviceAsin}<br />
                  <small className="text-muted">Type: {device.deviceType}</small>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  
  // Formatter for covered devices that only applies to extended warranties
  const coveredDevicesFormatter = (cell: any, row: any) => {
    if (row.coveredDeviceConfigs) {
      return <CoveredDevicesExpander coveredDeviceConfigs={row.coveredDeviceConfigs} />;
    }
    return null;
  };

  // Custom sort caret component to indicate sortable columns
  const sortCaret = (order: 'asc' | 'desc' | undefined) => {
    if (!order) return <span className="ms-1 text-muted"><CaretUpFill size={10} /></span>;
    return order === 'asc' ? 
      <span className="ms-1 text-primary"><CaretUpFill size={10} /></span> : 
      <span className="ms-1 text-primary"><CaretDownFill size={10} /></span>;
  };

  // Common columns shared between both warranty types
  const commonColumns: ColumnDescription[] = [
    {
      dataField: 'marketplaceId',
      text: 'Marketplace ID',
      sort: true,
      sortCaret,
      formatter: (cell) => {
        const countryCode = marketplaceToCountryMap[cell] || 'N/A';
        return `${cell} (${countryCode})`;
      }
    },
    {
      dataField: 'warrantyLengthYear',
      text: 'Length (Years)',
      sort: true,
      sortCaret
    },
    {
      dataField: 'warrantyLengthMonth',
      text: 'Length (Months)',
      sort: true,
      sortCaret,
      formatter: (cell) => cell === null || cell === undefined ? '0' : cell
    },
    {
      dataField: 'warrantyLengthDay',
      text: 'Length (Days)',
      sort: true,
      sortCaret,
      formatter: (cell) => cell === null || cell === undefined ? '0' : cell
    },
    {
      dataField: 'warrantyGracePeriodDay',
      text: 'Grace Period (Days)',
      sort: true,
      sortCaret
    }
  ];
  
  // Extended Warranty specific columns
  const extendedWarrantyColumns: ColumnDescription[] = [
    {
      dataField: 'warrantyAsin',
      text: 'Warranty ASIN',
      sort: true,
      sortCaret
    },
    ...commonColumns,
    {
      dataField: 'coveredDeviceConfigs',
      text: 'Covered Devices',
      formatter: coveredDevicesFormatter
    },
    {
      dataField: 'purchaseEligibilityDay',
      text: 'Purchase Eligibility (Days)',
      sort: true,
      sortCaret
    }
  ];
  
  // Limited Warranty specific columns
  const limitedWarrantyColumns: ColumnDescription[] = [
    {
      dataField: 'configurationDeviceAsin',
      text: 'Device ASIN',
      sort: true,
      sortCaret
    },
    ...commonColumns,
    {
      dataField: 'worryFree',
      text: 'Worry Free',
      sort: true,
      formatter: (cell) => {
        // Default to "No" (X mark)
        if (cell === undefined || cell === null) return '❌';
        // Return checkmark for Yes, X mark for No
        return cell === true ? '✅' : '❌';
      }
    },
    {
      dataField: 'deviceType',
      text: 'Device Type',
      sort: true,
      sortCaret
    }
  ];
  
  // Determine the data type from the loaded configurations rather than form selection
  const isExtendedData = configurations.length > 0 && instanceOfExtendedWarrantyConfiguration(configurations[0]);
  
  // Set the table key field based on the actual data, not the form selection
  const tableKeyField = isExtendedData ? 'warrantyAsin' : 'configurationDeviceAsin';
  
  // Get active columns based only on the actual data type
  const activeColumns = isExtendedData ? extendedWarrantyColumns : limitedWarrantyColumns;

  // Handle search form submission
  const handleSearch = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    // Marketplace is optional
    
    if (!asin) {
      dispatch(fetchConfigurationsFailure('Please enter an ASIN'));
      return;
    }
    
    if (!configType) {
      dispatch(fetchConfigurationsFailure('Please select a configuration type'));
      return;
    }
    
    try {
      dispatch(fetchConfigurationsStart());
      setSearchPerformed(true);
      
      const response = await getConfiguration({
        marketplaceId: selectedMarketplace || '', // Empty string when no marketplace is selected
        asin: asin,
        configType: configType
      });
      
      if (response.status === 200 && response.data.success) {
        dispatch(fetchConfigurationsSuccess(response.data.configurations || []));
      } else {
        // Get error message(s), allowing for array return when multiple errors exist
        const errorMessages = parseApiError(response, true);
        dispatch(fetchConfigurationsFailure(errorMessages));
      }
    } catch (error) {
      // Format the error and ensure it works with our updated action
      const errorMessage = formatError(error);
      dispatch(fetchConfigurationsFailure(errorMessage));
    }
  };

  // Clear search form
  const handleClear = () => {
    setAsin('');
    setConfigType('');
    setSearchPerformed(false);
  };

  return (
    <Container className="mt-4">
      <h2>Configuration Viewer</h2>
      <p>Search and view warranty configurations.</p>
      
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Marketplace (Optional)</Form.Label>
                  <MarketplaceSelector 
                    selectedOption={selectedMarketplace}
                    formValueSetter={setSelectedMarketplace}
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Device or Extended Warranty Asin <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter Asin" 
                    value={asin}
                    onChange={(e) => setAsin(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Configuration Type <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={configType}
                    onChange={(e) => setConfigType(e.target.value)}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="EXTENDED">Extended Warranty</option>
                    <option value="LIMITED">Limited Warranty</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleClear}>
                Clear
              </Button>
              <Button variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Searching...
                  </>
                ) : (
                  'Search'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      {/* Error Message */}
      {errors && errors.length > 0 && (
        <ErrorMessage
          message={errors[0]}
          messages={errors}
          title="Search Error"
          onRetry={handleSearch}
          onDismiss={() => dispatch(fetchConfigurationsFailure(['']))}
          showRetry={true}
          showDismiss={true}
        />
      )}
      
      {/* Results */}
      {searchPerformed && !isLoading && (!errors || errors.length === 0) && (
        <>
          <h3>Search Results</h3>
          {configurations.length === 0 ? (
            <Alert variant="info">
              No configurations found matching your search criteria.
            </Alert>
          ) : (
            <APIResultViewer
              data={configurations}
              title="Warranty Configurations"
              keyField={tableKeyField}
              columns={activeColumns}
            />
          )}
        </>
      )}
      
      {/* Loading Indicator */}
      {isLoading && (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading configurations...</p>
        </div>
      )}
    </Container>
  );
};

export default ConfigurationViewerPage;
